import { SEARCH_CATEGORY_FILTER, SEARCH_FILTER_TITLE_PREFIX } from '@shared/system-constants';
import { ISearchCategoryFilter } from '@shared/types/search';
import {
  ISearchIOAggregationCount,
  ISearchResponse,
} from '@unified-commerce/gpc-vue-storefront-search';
import { Collection } from '@unified-commerce/gpc-vue-storefront-shopify';

import {
  SEARCH_CATEGORY_HANDLE_SEPARATOR,
  SEARCH_CATEGORY_PATH_SEPERATOR,
  SEARCH_CATEGORY_TENANT_PREFIX_HANDLE,
  SEARCH_CATEGORY_TENANT_PREFIX_TITLE,
} from '~/constants';

/** Extracts category (algolia collections) titles and counts from the search response. */
function extractSearchCollections(searchQueryResponse: ISearchResponse) {
  const filterKey = `${SEARCH_FILTER_TITLE_PREFIX}${SEARCH_CATEGORY_FILTER}`;
  return searchQueryResponse?.aggregateFilters?.[filterKey]?.count?.counts;
}

/** Maps the category collection handles from the search results to matching shopify category titles. */
const mapSearchHandlesToShopifyTitles = (
  searchCategories: ISearchIOAggregationCount,

  shopifyCategoriesResponse: readonly Collection[],
) => {
  return Object.fromEntries(
    Object.entries(searchCategories).map(([searchHandle, count]) => {
      const match = shopifyCategoriesResponse.find((category) => category.handle === searchHandle);
      const title = match ? match.title : searchHandle;
      return [title, count];
    }),
  );
};

/** Returns an array of ancestor handles for a given category title. */
function buildAncestorHandles(title: string, shopifyCategories: readonly Collection[]): string[] {
  const segments = title.split(SEARCH_CATEGORY_PATH_SEPERATOR);
  // Must include at least: tenant, parent, current category
  if (segments.length < 3) return [];
  // Remove the first (tenant) and last (current category) segments
  const ancestorSegments = segments.slice(1, -1);
  return ancestorSegments.map((_, index) => {
    // Build the full path for this ancestor level (e.g., "AMX Categories>Parent>Child")
    const pathToAncestor = ancestorSegments.slice(0, index + 1);
    const fullTitle = [SEARCH_CATEGORY_TENANT_PREFIX_TITLE, ...pathToAncestor].join(
      SEARCH_CATEGORY_PATH_SEPERATOR,
    ); // Find the matching Shopify category using the full title
    const matchedCategory = shopifyCategories.find((cat) => cat.title === fullTitle); // Return the handle with the tenant prefix removed, or fallback to empty string
    return matchedCategory?.handle.replace(SEARCH_CATEGORY_TENANT_PREFIX_HANDLE, '') || '';
  });
}

/** Builds all valid category filter objects using Shopify metadata and search counts. */
function buildAllCategoryFilters(
  titlesWithCount: Record<string, number>,
  shopifyCategories: readonly Collection[],
  countsMap: Record<string, number>,
  getCategoryExactTitle: (categoryTitle: string) => string,
  getEncodedCategoryHandle: (categoryHandle: string) => string,
): ISearchCategoryFilter[] {
  return Object.entries(titlesWithCount)
    .map(([title, count]) =>
      buildCategoryFilter(
        title,
        count,
        shopifyCategories,
        countsMap,
        getCategoryExactTitle,
        getEncodedCategoryHandle,
      ),
    )
    .filter((f): f is ISearchCategoryFilter => f !== null)
    .sort((a, b) => a.exactHandle.localeCompare(b.exactHandle));
}

/** Builds a single category filter object from title, counts, and collection metadata. */
function buildCategoryFilter(
  title: string,
  count: number,
  shopifyCategories: readonly Collection[],
  countsMap: Record<string, number>,
  getCategoryExactTitle: (categoryTitle: string) => string,
  getEncodedCategoryHandle: (categoryHandle: string) => string,
): ISearchCategoryFilter | null {
  const matched = shopifyCategories.find((cat) => cat.title === title);
  if (
    !matched ||
    !title.startsWith(SEARCH_CATEGORY_TENANT_PREFIX_TITLE) ||
    !title.includes(SEARCH_CATEGORY_PATH_SEPERATOR)
  )
    return null;
  const exactTitle = getCategoryExactTitle(title);
  const level = title.split(SEARCH_CATEGORY_PATH_SEPERATOR).length - 1;
  const ancestors = level > 1 ? buildAncestorHandles(title, shopifyCategories) : [];
  const exactHandle = matched.handle.replace(SEARCH_CATEGORY_TENANT_PREFIX_HANDLE, '');

  return {
    id: matched.id,
    encodedHandle: getEncodedCategoryHandle(exactHandle),
    exactHandle,
    urlPath: getCategoryURLPath(ancestors, exactHandle),
    exactTitle,
    level,
    ancestors,
    productCount: countsMap[title] ?? count,
  };
}

const MAX_NUMBER_OF_CATEGORIES = 6;
/** Filters, sorts, and slices, the category filters for usage. */
function pruneCategoryFilters(
  filters: ISearchCategoryFilter[],
  fromSearchResults: boolean,
  preApplied: string,
): ISearchCategoryFilter[] {
  const filtered = filters.filter((f) => !preApplied.includes(f.exactTitle));
  if (fromSearchResults) {
    return filtered
      .filter((f) => !f.urlPath.startsWith('/brand-categories'))
      .sort((a, b) => b.productCount - a.productCount)
      .slice(0, MAX_NUMBER_OF_CATEGORIES);
  }
  return filtered;
}

/** internal :: Retrieves the URL path for the provided category. */
const getCategoryURLPath = (ancestorHandles: string[], categoryHandle: string): string => {
  const filteredHandles = ancestorHandles.filter((handle) => handle !== '');
  if (filteredHandles.length > 0) {
    const urlPaths = [...filteredHandles, categoryHandle].map((handle, i, handles) => {
      const previousHandle = handles[i - 1];
      return handle.replace(previousHandle + SEARCH_CATEGORY_HANDLE_SEPARATOR, '');
    });
    return `/${urlPaths.join('/')}`;
  }
  return `/${categoryHandle}`;
};

export default {
  extractSearchCollections,
  mapSearchHandlesToShopifyTitles,
  buildAllCategoryFilters,
  pruneCategoryFilters,
};
